import { Heightfield } from '../terrain/heightfield'
import type { Skier } from './skier'
import { TUNE } from './tuning'
import { clamp01 } from '../core/math'

/**
 * Sluff. Spec §6 — "the mechanic that makes this specific".
 *
 * "When you ski steep soft snow, you release loose snow that accelerates
 * downhill behind you. Real freeriders manage it constantly: ski to the side of
 * your sluff, don't let it catch you, don't get pushed into a terrain trap."
 *
 * What it buys, per the spec:
 *   - urgency without a timer, because the pressure is diegetic
 *   - a direct punishment for hesitation, which feeds the fluidity metric
 *   - reward for a real technique: cutting across the fall line, using ridges
 *     and spines to shed it
 *
 * Implementation is deliberately the cheap one the spec describes: "a particle
 * mass following a downhill flow field, with a simple caught state". No fluid
 * sim. Deterministic and Three-free, because the solver and the server both
 * run it.
 */

/**
 * Particle budget.
 *
 * This is a *physics constant*, not a quality setting: the count changes how
 * much snow is in the air and therefore how hard sluff loads the skier, so a
 * run recorded at one count does not replay at another. Solver, live play and
 * ghost playback must all use the same number or every ghost desyncs.
 */
export const SLUFF_PARTICLES = 140

/** Branchable sluff state, so the par solver can fork mid-run. */
export interface SluffSnapshot {
  data: Float32Array
  count: number
  cursor: number
  carry: number
  seed: number
}

export class Sluff {
  /** Live particle count, for the renderer and the debug overlay. */
  count = 0

  private readonly n: number
  private readonly px: Float32Array
  private readonly py: Float32Array
  private readonly pz: Float32Array
  private readonly vx: Float32Array
  private readonly vz: Float32Array
  /** Mass, 0 = dead. Sluff entrains more snow as it runs and then runs out. */
  private readonly mass: Float32Array
  private readonly life: Float32Array

  private cursor = 0
  private carry = 0
  /** Deterministic jitter stream — never Math.random(). */
  private seed = 0x1a2b3c4d

  /**
   * @param particles overriding this desynchronises ghosts — see SLUFF_PARTICLES.
   */
  constructor(private readonly hf: Heightfield, particles = SLUFF_PARTICLES) {
    this.n = particles
    this.px = new Float32Array(particles)
    this.py = new Float32Array(particles)
    this.pz = new Float32Array(particles)
    this.vx = new Float32Array(particles)
    this.vz = new Float32Array(particles)
    this.mass = new Float32Array(particles)
    this.life = new Float32Array(particles)
  }

  save(out?: SluffSnapshot): SluffSnapshot {
    const data = out?.data ?? new Float32Array(this.n * 7)
    data.set(this.px, 0)
    data.set(this.py, this.n)
    data.set(this.pz, this.n * 2)
    data.set(this.vx, this.n * 3)
    data.set(this.vz, this.n * 4)
    data.set(this.mass, this.n * 5)
    data.set(this.life, this.n * 6)
    return { data, count: this.count, cursor: this.cursor, carry: this.carry, seed: this.seed }
  }

  load(s: SluffSnapshot): void {
    const n = this.n
    this.px.set(s.data.subarray(0, n))
    this.py.set(s.data.subarray(n, n * 2))
    this.pz.set(s.data.subarray(n * 2, n * 3))
    this.vx.set(s.data.subarray(n * 3, n * 4))
    this.vz.set(s.data.subarray(n * 4, n * 5))
    this.mass.set(s.data.subarray(n * 5, n * 6))
    this.life.set(s.data.subarray(n * 6, n * 7))
    this.count = s.count
    this.cursor = s.cursor
    this.carry = s.carry
    this.seed = s.seed
  }

  reset(): void {
    this.mass.fill(0)
    this.life.fill(0)
    this.count = 0
    this.carry = 0
    this.cursor = 0
    this.seed = 0x1a2b3c4d
  }

  private rand(): number {
    let s = this.seed
    s ^= s << 13
    s ^= s >>> 17
    s ^= s << 5
    this.seed = s >>> 0
    return this.seed / 4294967296
  }

  /** Read-only views for the renderer. */
  get positionsX(): Float32Array { return this.px }
  get positionsY(): Float32Array { return this.py }
  get positionsZ(): Float32Array { return this.pz }
  get masses(): Float32Array { return this.mass }
  get capacity(): number { return this.n }

  /**
   * Release snow from under the skier's skis and advance what is already
   * running. Returns the load on the skier, 0..1, which the solver reads too.
   */
  step(skier: Skier, dt: number): number {
    const hf = this.hf

    // --- Release ------------------------------------------------------------
    // You only sluff what is loose, on ground steep enough to shed it, and only
    // while you are actually cutting snow.
    if (!skier.airborne && skier.crashTimer <= 0) {
      const prone = hf.sampleSluffProne(skier.x, skier.z)
      const cutting = clamp01(skier.speed / 12) * (0.35 + clamp01(skier.gripUsed) * 0.9)
      const rate = prone * cutting * 42
      this.carry += rate * dt
      const fwdX = Math.sin(skier.heading)
      const fwdZ = Math.cos(skier.heading)
      let n = Math.floor(this.carry)
      this.carry -= n
      if (n > 6) n = 6
      for (let k = 0; k < n; k++) this.spawn(skier, fwdX, fwdZ, prone)
    }

    // --- Advance ------------------------------------------------------------
    const G = TUNE.GRAVITY * TUNE.SLUFF_GRAVITY
    const nrm = { x: 0, y: 1, z: 0 }
    let live = 0
    let load = 0

    const reach2 = TUNE.SLUFF_REACH * TUNE.SLUFF_REACH

    for (let i = 0; i < this.n; i++) {
      if (this.mass[i] <= 0) continue

      this.life[i] += dt
      let x = this.px[i]
      let z = this.pz[i]

      if (!hf.inBounds(x, z)) { this.mass[i] = 0; continue }

      hf.sampleNormal(x, z, nrm)
      // Flow field: the horizontal part of the surface normal is the downhill
      // direction. Ridges and spines shed sluff sideways for free, which is
      // exactly the technique the mechanic is meant to reward.
      this.vx[i] += nrm.x * G * dt
      this.vz[i] += nrm.z * G * dt

      const sp = Math.hypot(this.vx[i], this.vz[i])
      const drag = TUNE.SLUFF_DRAG_SELF * (1 + sp * 0.03)
      this.vx[i] -= this.vx[i] * drag * dt
      this.vz[i] -= this.vz[i] * drag * dt

      x += this.vx[i] * dt
      z += this.vz[i] * dt
      this.px[i] = x
      this.pz[i] = z
      this.py[i] = hf.sampleHeight(x, z)

      // Entrain more loose snow while running on sluff-prone ground, and die
      // out on the flats — which is what makes a terrain trap dangerous: the
      // sluff arrives there and stops, with you in it.
      const prone = hf.sampleSluffProne(x, z)
      this.mass[i] += (prone * 0.55 - 0.42) * dt
      if (this.life[i] > 9 || this.mass[i] <= 0 || sp < 0.4) { this.mass[i] = 0; continue }
      if (this.mass[i] > 1.6) this.mass[i] = 1.6

      live++

      // --- Does it have the skier? -----------------------------------------
      const dx = skier.x - x
      const dz = skier.z - z
      const d2 = dx * dx + dz * dz
      if (d2 < reach2) {
        // Only snow moving *into* you counts. Outrunning your sluff is the
        // whole skill; being beside it is safe.
        const rel = this.vx[i] * dx + this.vz[i] * dz
        if (rel > 0) {
          const proximity = 1 - Math.sqrt(d2) / TUNE.SLUFF_REACH
          load += this.mass[i] * proximity * TUNE.SLUFF_LOAD_PER_PARTICLE
        }
      }
    }

    this.count = live
    return clamp01(load)
  }

  private spawn(skier: Skier, fwdX: number, fwdZ: number, prone: number): void {
    const i = this.cursor
    this.cursor = (this.cursor + 1) % this.n
    const back = TUNE.SLUFF_SPAWN_BACK * (0.5 + this.rand())
    const side = (this.rand() - 0.5) * 5.5
    // Perpendicular to travel, so the release reads as a fan off the tails.
    this.px[i] = skier.x - fwdX * back - fwdZ * side
    this.pz[i] = skier.z - fwdZ * back + fwdX * side
    this.py[i] = this.hf.sampleHeight(this.px[i], this.pz[i])
    // Starts near rest: sluff has to build speed, which is what gives you the
    // chance to stay ahead of it.
    this.vx[i] = skier.vx * 0.12
    this.vz[i] = skier.vz * 0.12
    this.mass[i] = 0.35 + prone * 0.5
    this.life[i] = 0
  }
}
